import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Send, Loader2 } from "lucide-react";
import { MaskedLine, Overline, FadeUp } from "@/components/site/Reveal";
import { postContact } from "@/lib/api";
import { INQUIRY_OPTIONS } from "@/data/content";

const inputCls =
  "w-full border border-white/20 bg-black/40 px-5 py-4 text-sm text-white placeholder:text-white/30 outline-none transition-colors duration-300 focus:border-brand-accent focus:ring-1 focus:ring-brand-accent";

export default function Contact() {
  const [params] = useSearchParams();
  const initialType = INQUIRY_OPTIONS.some((o) => o.value === params.get("type")) ? params.get("type") : "general";
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", inquiry_type: initialType, message: "" });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await postContact(form);
      toast.success("Message sent — we'll be in touch soon.");
      setForm({ name: "", email: "", phone: "", company: "", inquiry_type: form.inquiry_type, message: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <main className="mx-auto max-w-7xl px-5 pb-24 pt-36 md:px-8 md:pb-32 md:pt-48" data-testid="contact-page">
      <div className="grid gap-16 lg:grid-cols-2">
        <div>
          <MaskedLine>
            <Overline>Get In Touch</Overline>
          </MaskedLine>
          <h1 className="mt-8 font-display text-5xl font-black uppercase leading-[0.9] tracking-tighter sm:text-6xl lg:text-7xl" data-testid="contact-heading">
            <MaskedLine delay={0.15}>Talk</MaskedLine>
            <MaskedLine delay={0.3}><span className="text-brand-accent">to us.</span></MaskedLine>
          </h1>
          <FadeUp delay={0.5}>
            <p className="mt-8 max-w-md text-base leading-relaxed text-muted md:text-lg">
              Sponsorships, partnerships, guest requests, or just a good course recommendation —
              this is the first tee. Send it.
            </p>
            <div className="mt-12 flex flex-col gap-6">
              {[
                { label: "Sponsorships", desc: "Put your brand in front of golfers who buy." },
                { label: "Partnerships", desc: "Events, series, collaborations, course trips." },
                { label: "Guest Requests", desc: "Founders, athletes, personalities — come on the show." },
              ].map((item) => (
                <div key={item.label} className="border-l-2 border-brand pl-5" data-testid={`contact-topic-${item.label.toLowerCase().replace(/\s/g, "-")}`}>
                  <p className="font-mono text-xs uppercase tracking-[0.25em] text-brand-accent">{item.label}</p>
                  <p className="mt-2 text-sm text-muted">{item.desc}</p>
                </div>
              ))}
            </div>
          </FadeUp>
        </div>

        <FadeUp delay={0.35}>
          <form onSubmit={submit} className="border border-white/10 bg-ink-2 p-7 md:p-10" data-testid="contact-form">
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-muted">I'm reaching out about</p>
            <div className="mt-4 flex flex-wrap gap-2" data-testid="contact-type-picker">
              {INQUIRY_OPTIONS.map((o) => (
                <button
                  type="button"
                  key={o.value}
                  onClick={() => setForm((f) => ({ ...f, inquiry_type: o.value }))}
                  data-testid={`contact-type-${o.value}`}
                  className={`relative px-4 py-2.5 font-mono text-[11px] uppercase tracking-[0.2em] transition-colors duration-300 ${
                    form.inquiry_type === o.value ? "text-ink" : "border border-white/20 text-muted hover:border-white/50 hover:text-white"
                  }`}
                >
                  {form.inquiry_type === o.value && (
                    <motion.span layoutId="type-pill" className="absolute inset-0 bg-brand-accent" transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }} />
                  )}
                  <span className="relative z-10">{o.label}</span>
                </button>
              ))}
            </div>

            <div className="mt-8 grid gap-5 sm:grid-cols-2">
              <div>
                <label htmlFor="contact-name" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.25em] text-muted">Name *</label>
                <input id="contact-name" data-testid="contact-name-input" required value={form.name} onChange={set("name")} placeholder="Your name" className={inputCls} />
              </div>
              <div>
                <label htmlFor="contact-email" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.25em] text-muted">Email *</label>
                <input id="contact-email" data-testid="contact-email-input" required type="email" value={form.email} onChange={set("email")} placeholder="you@company.com" className={inputCls} />
              </div>
              <div>
                <label htmlFor="contact-phone" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.25em] text-muted">Phone</label>
                <input id="contact-phone" data-testid="contact-phone-input" type="tel" value={form.phone} onChange={set("phone")} placeholder="(555) 555-5555" className={inputCls} />
              </div>
            </div>

            <div className="mt-5">
              <label htmlFor="contact-company" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.25em] text-muted">Company</label>
              <input id="contact-company" data-testid="contact-company-input" value={form.company} onChange={set("company")} placeholder="Company or brand (optional)" className={inputCls} />
            </div>

            <div className="mt-5">
              <label htmlFor="contact-message" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.25em] text-muted">Message *</label>
              <textarea id="contact-message" data-testid="contact-message-input" required rows={5} value={form.message} onChange={set("message")} placeholder="Tell us what you have in mind…" className={`${inputCls} resize-none`} />
            </div>

            <button
              type="submit"
              disabled={sending}
              data-testid="contact-submit-button"
              className="group mt-8 flex w-full items-center justify-center gap-3 bg-white px-7 py-4 font-mono text-xs font-medium uppercase tracking-[0.2em] text-ink transition-colors duration-300 hover:bg-brand-accent disabled:opacity-60"
            >
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />}
              {sending ? "Sending…" : "Send Message"}
            </button>
          </form>
        </FadeUp>
      </div>
    </main>
  );
}
